import { useMemo } from "react";

import { ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { FieldLabel, SupportIcon } from "modules/DMSv3/Capabilities/components/DMSCapabilitiesTable/components";
import "modules/DMSv3/Capabilities/components/DMSCapabilitiesTable/DMSCapabilitiesTable.scss";
import { DEFAULT_SELECTED_DMS, useDMSCapabilitiesQuery, useSelectedDMSIds } from "modules/DMSv3/Capabilities/hooks";
import { getDmsName } from "utils/dms";

import type { AccessorColumnDef } from "@tanstack/react-table";
import type { DMSIDS, DMS_CAPABILITY_SUPPORTED_STATUSES } from "models";
import type { DMSCapability } from "modules/DMSv3/Capabilities/queryKeys";

export enum DMS_MATRIX_ROW_KINDS {
  Category = 1,
  Field = 2,
  Spacer = 3,
  Action = 4
}

export type DMSMatrixRow = {
  kind: DMS_MATRIX_ROW_KINDS;
  object: string;
  action: string;
  row_key: string;
  label: string;
  description: string;
};

export const getRowKey = (object: string, action: string, field: string) => `${object}.${action}.${field}`;
const getSupportKey = (dmsId: DMSIDS, rowKey: string) => `${dmsId}|${rowKey}`;
const getDMSColumnId = (dmsId: DMSIDS) => `dms-${dmsId}`;

export const getSupportMap = (capabilities: DMSCapability[]) =>
  capabilities.reduce((supportMap, { id, capabilities: dmsCapabilities }) => {
    dmsCapabilities.forEach(({ object, action, field, supported }) => supportMap.set(getSupportKey(id, getRowKey(object, action, field)), supported));

    return supportMap;
  }, new Map<string, DMS_CAPABILITY_SUPPORTED_STATUSES>());

export const getMatrixRows = (capabilities: DMSCapability[]) => {
  const descriptionsByObject = new Map<string, Map<string, Map<string, string>>>();

  capabilities.forEach(({ capabilities: dmsCapabilities }) =>
    dmsCapabilities.forEach(({ object, action, field, field_description }) => {
      const descriptionsByAction = descriptionsByObject.get(object) ?? new Map<string, Map<string, string>>();
      const descriptionsByField = descriptionsByAction.get(action) ?? new Map<string, string>();

      if (!descriptionsByField.has(field)) descriptionsByField.set(field, field_description);
      descriptionsByAction.set(action, descriptionsByField);
      descriptionsByObject.set(object, descriptionsByAction);
    })
  );

  return Array.from(descriptionsByObject).reduce<DMSMatrixRow[]>((rows, [object, descriptionsByAction]) => {
    if (rows.length) rows.push({ kind: DMS_MATRIX_ROW_KINDS.Spacer, object, action: "", row_key: `${object}.spacer`, label: "", description: "" });

    rows.push({ kind: DMS_MATRIX_ROW_KINDS.Category, object, action: "", row_key: object, label: object, description: "" });

    descriptionsByAction.forEach((descriptionsByField, action) => {
      rows.push({ kind: DMS_MATRIX_ROW_KINDS.Action, object, action, row_key: `${object}.${action}`, label: action, description: "" });

      descriptionsByField.forEach((description, field) =>
        rows.push({
          kind: DMS_MATRIX_ROW_KINDS.Field,
          object,
          action,
          row_key: getRowKey(object, action, field),
          label: field,
          description
        })
      );
    });

    return rows;
  }, []);
};

export const DMSCapabilitiesTable = () => {
  const { data: capabilities = [], isFetching, refetch } = useDMSCapabilitiesQuery();
  const { data: selectedDMSIds } = useSelectedDMSIds();

  useRegisterNavAction(() => refetch());

  const rows = useMemo(() => getMatrixRows(capabilities), [capabilities]);
  const supportMap = useMemo(() => getSupportMap(capabilities), [capabilities]);

  const columns = useMemo<AccessorColumnDef<DMSMatrixRow>[]>(
    () => [
      {
        id: "field",
        accessorFn: row => row,
        size: 280,
        header: () => "",
        cell: ({ row: { original } }) => <FieldLabel row={original} />
      },
      ...capabilities.map<AccessorColumnDef<DMSMatrixRow>>(({ id }) => ({
        id: getDMSColumnId(id),
        accessorFn: row => row,
        size: 90,
        header: () => <span className="no-wrap-text">{getDmsName(id)}</span>,
        cell: ({ row: { original } }) =>
          original.kind === DMS_MATRIX_ROW_KINDS.Field ? <SupportIcon isSupported={supportMap.get(getSupportKey(id, original.row_key))} /> : null
      }))
    ],
    [capabilities, supportMap]
  );

  const columnVisibility = useMemo(
    () =>
      capabilities.reduce<Record<string, boolean>>((visibility, { id }) => {
        visibility[getDMSColumnId(id)] = selectedDMSIds?.includes(id) ?? DEFAULT_SELECTED_DMS.includes(id);

        return visibility;
      }, {}),
    [capabilities, selectedDMSIds]
  );

  return (
    <div className="DMSCapabilitiesTable">
      <ReactTable isFetching={isFetching} columns={columns} data={rows} state={{ columnVisibility }} />
    </div>
  );
};
